import { Action, createAction, createAsyncAction, createReducer } from 'typesafe-actions';
import { produce } from 'immer';
import type { ReduxError } from '@universal/errors';
import {
  ConsumerGuidesListResponse,
  ConsumerGuideDetail,
  ConsumerTestedProduct,
  ConsumerTestedProductsResponse,
} from '../../../../types/consumerTestType';

// Actions
export enum Actions {
  // Get all guides
  GET_CONSUMER_GUIDES = 'naf/GET_CONSUMER_GUIDES',
  GET_CONSUMER_GUIDES_SUCCESS = 'naf/GET_CONSUMER_GUIDES_SUCCESS',
  GET_CONSUMER_GUIDES_FAIL = 'naf/GET_CONSUMER_GUIDES_FAIL',
  GET_CONSUMER_GUIDES_CANCEL = 'naf/GET_CONSUMER_GUIDES_CANCEL',
  // Get specific guide
  GET_ONE_GUIDE = 'naf/GET_ONE_GUIDE',
  GET_ONE_GUIDE_SUCCESS = 'naf/GET_ONE_GUIDE_SUCCESS',
  GET_ONE_GUIDE_FAIL = 'naf/GET_ONE_GUIDE_FAIL',
  GET_ONE_GUIDE_CANCEL = 'naf/GET_ONE_GUIDE_CANCEL',
  // Get products from specific guide
  GET_TESTED_PRODUCTS = 'naf/GET_TESTED_PRODUCTS',
  GET_TESTED_PRODUCTS_SUCCESS = 'naf/GET_TESTED_PRODUCTS_SUCCESS',
  GET_TESTED_PRODUCTS_FAIL = 'naf/GET_TESTED_PRODUCTS_FAIL',
  GET_TESTED_PRODUCTS_CANCEL = 'naf/GET_TESTED_PRODUCTS_CANCEL',
  // Get specific tested product
  GET_ONE_PRODUCT = 'naf/GET_ONE_PRODUCT',
  GET_ONE_PRODUCT_SUCCESS = 'naf/GET_ONE_PRODUCT_SUCCESS',
  GET_ONE_PRODUCT_FAIL = 'naf/GET_ONE_PRODUCT_FAIL',
  GET_ONE_PRODUCT_CANCEL = 'naf/GET_ONE_PRODUCT_CANCEL',
  // Get related products from specific guide to show in sidebar
  GET_RELATED_PRODUCTS = 'naf/GET_RELATED_PRODUCTS',
  GET_RELATED_PRODUCTS_SUCCESS = 'naf/GET_RELATED_PRODUCTS_SUCCESS',
  GET_RELATED_PRODUCTS_FAIL = 'naf/GET_RELATED_PRODUCTS_FAIL',
  GET_RELATED_PRODUCTS_CANCEL = 'naf/GET_RELATED_PRODUCTS_CANCEL',
  // Clear selected guide
  CLEAR_SELECTED_GUIDE = 'naf/CLEAR_SELECTED_GUIDE',
  // Clear tested products
  CLEAR_TESTED_PRODUCTS = 'naf/CLEAR_TESTED_PRODUCTS',
  // Clear tested products
  CLEAR_RELATED_PRODUCTS = 'naf/CLEAR_RELATED_PRODUCTS',
}

export interface State {
  data: ConsumerGuidesListResponse;
  selectedGuide?: ConsumerGuideDetail;
  oneProduct?: ConsumerTestedProduct;
  testedProducts?: ConsumerTestedProductsResponse;
  relatedProducts?: ConsumerTestedProductsResponse;
  // Guide the related products were last requested for.
  relatedProductsGuideId?: string;
  isUpdatingGuide?: boolean;
  isUpdatingProducts?: boolean;
  errorState?: any;
}

export const initialState: State = {
  data: {
    items: [],
  },
  selectedGuide: {},
  oneProduct: {},
  testedProducts: { items: [], itemsCount: 0, filters: [] },
  relatedProducts: { items: [], itemsCount: 0 },
  isUpdatingGuide: false,
  isUpdatingProducts: false,
  errorState: null,
};

export const actions = {
  getConsumerGuides: createAsyncAction(
    Actions.GET_CONSUMER_GUIDES, // request payload creator
    Actions.GET_CONSUMER_GUIDES_SUCCESS, // success payload creator
    Actions.GET_CONSUMER_GUIDES_FAIL, // failure payload creator
    Actions.GET_CONSUMER_GUIDES_CANCEL, // optional cancel payload creator
  )<undefined, ConsumerGuidesListResponse, ReduxError>(),
  getOneGuide: createAsyncAction(
    Actions.GET_ONE_GUIDE, // request payload creator
    Actions.GET_ONE_GUIDE_SUCCESS, // success payload creator
    Actions.GET_ONE_GUIDE_FAIL, // failure payload creator
    Actions.GET_ONE_GUIDE_CANCEL, // optional cancel payload creator
  )<string, ConsumerGuideDetail, ReduxError>(),
  getTestedProducts: createAsyncAction(
    Actions.GET_TESTED_PRODUCTS, // request payload creator
    Actions.GET_TESTED_PRODUCTS_SUCCESS, // success payload creator
    Actions.GET_TESTED_PRODUCTS_FAIL, // failure payload creator
    Actions.GET_TESTED_PRODUCTS_CANCEL, // optional cancel payload creator
  )<string, ConsumerTestedProductsResponse, ReduxError>(),
  getOneProduct: createAsyncAction(
    Actions.GET_ONE_PRODUCT, // request payload creator
    Actions.GET_ONE_PRODUCT_SUCCESS, // success payload creator
    Actions.GET_ONE_PRODUCT_FAIL, // failure payload creator
    Actions.GET_ONE_PRODUCT_CANCEL, // optional cancel payload creator
  )<string, ConsumerTestedProduct, ReduxError>(),
  getRelatedProducts: createAsyncAction(
    Actions.GET_RELATED_PRODUCTS, // request payload creator
    Actions.GET_RELATED_PRODUCTS_SUCCESS, // success payload creator
    Actions.GET_RELATED_PRODUCTS_FAIL, // failure payload creator
    Actions.GET_RELATED_PRODUCTS_CANCEL, // optional cancel payload creator
  )<string, ConsumerTestedProductsResponse, ReduxError, undefined>(),
  clearTestedProducts: createAction(Actions.CLEAR_TESTED_PRODUCTS)(),
  clearRelatedProducts: createAction(Actions.CLEAR_RELATED_PRODUCTS)(),
  clearSelectedGuide: createAction(Actions.CLEAR_SELECTED_GUIDE)(),
};

export const reducers = createReducer<State, Action>(initialState, {})
  // Get all guides
  .handleAction(actions.getConsumerGuides.request, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.isUpdatingGuide = true;
    }),
  )
  .handleAction(actions.getConsumerGuides.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.isUpdatingGuide = false;
      draftState.data = action.payload;
    }),
  )
  .handleAction(actions.getConsumerGuides.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.isUpdatingGuide = false;
      draftState.errorState = action.payload;
    }),
  )
  .handleAction(actions.getConsumerGuides.cancel, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.isUpdatingGuide = false;
    }),
  )
  // Get specific guide
  .handleAction(actions.getOneGuide.request, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.isUpdatingGuide = true;
      draftState.errorState = false;
    }),
  )
  .handleAction(actions.getOneGuide.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.isUpdatingGuide = false;
      draftState.errorState = false;
      draftState.selectedGuide = action.payload;
    }),
  )
  .handleAction(actions.getOneGuide.failure, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.isUpdatingGuide = false;
      draftState.errorState = true;
    }),
  )
  .handleAction(actions.getOneGuide.cancel, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.isUpdatingGuide = false;
    }),
  )
  // Get tested products in specific guide
  .handleAction(actions.getTestedProducts.request, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.isUpdatingProducts = true;
    }),
  )
  .handleAction(actions.getTestedProducts.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.isUpdatingProducts = false;
      draftState.testedProducts = {
        items: action.payload.items || [],
        itemsCount: action.payload.itemsCount || 0,
        filters: action.payload.filters || [],
      };
    }),
  )
  .handleAction(actions.getTestedProducts.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.isUpdatingProducts = false;
      draftState.errorState = action.payload.message;
    }),
  )
  .handleAction(actions.getTestedProducts.cancel, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.isUpdatingProducts = false;
    }),
  )
  // Get single tested product
  .handleAction(actions.getOneProduct.request, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.isUpdatingProducts = true;
    }),
  )
  .handleAction(actions.getOneProduct.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.isUpdatingProducts = false;
      draftState.oneProduct = action.payload;
    }),
  )
  .handleAction(actions.getOneProduct.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.isUpdatingProducts = false;
      draftState.errorState = action.payload.message;
    }),
  )
  .handleAction(actions.getOneProduct.cancel, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.isUpdatingProducts = false;
    }),
  )
  // Get related products in specific guide to show in sidebar
  .handleAction(actions.getRelatedProducts.request, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.isUpdatingProducts = true;
      draftState.relatedProductsGuideId = action.payload;
    }),
  )
  .handleAction(actions.getRelatedProducts.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.isUpdatingProducts = false;
      draftState.relatedProducts = {
        items: action.payload.items || [],
        itemsCount: action.payload.itemsCount || 0,
      };
    }),
  )

  .handleAction(actions.getRelatedProducts.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.isUpdatingProducts = false;
      draftState.errorState = action.payload.message;
    }),
  )
  .handleAction(actions.getRelatedProducts.cancel, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.isUpdatingProducts = false;
    }),
  )
  // Clear selected guide
  .handleAction(actions.clearSelectedGuide, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.selectedGuide = undefined;
    }),
  )
  .handleAction(actions.clearTestedProducts, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.testedProducts = { items: [], itemsCount: 0, filters: [] };
    }),
  )
  .handleAction(actions.clearRelatedProducts, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.relatedProducts = { items: [], itemsCount: 0 };
      draftState.relatedProductsGuideId = undefined;
    }),
  );
